import * as css from "./styles/userFormStyles.module.scss";

import {Accordion} from "@zendeskgarden/react-accordions";
import {Button} from "@zendeskgarden/react-buttons";
import {Separator} from "@zendeskgarden/react-dropdowns";
import {Field, Label, Toggle} from "@zendeskgarden/react-forms";
import {Inline} from "@zendeskgarden/react-loaders";
import {Body, Footer, FooterItem, Header} from "@zendeskgarden/react-modals";
import {UnorderedList} from "@zendeskgarden/react-typography";
import {ReactElement, useContext, useEffect, useState} from "react";
import {ThemeProvider} from "styled-components";
import {
    useListAuthorizationsLazyQuery,
    useListGroupsQuery,
    useListUserClientsPropertiesQuery,
    useListUserClientsQuery,
    UserGroupModel,
    UserRole
} from "../../../../../__generated__/generated_types";
import {CustomZDDropdown} from "../../../../../atoms/custom-zd-dropdown/CustomZDDropdown";
import {COLORS} from "../../../../../constants/Colors";
import {asMinimalProperty, MinimalProperty} from "../../../../../contexts/properties/logic";
import {formatterEnumToTitle} from "../../../../../utils/formatters";
import {ViziblyTheme} from "../../../../analyst/ViziblyZDGTheme";
import {authorizationTitleRemapping} from "../../logic/authorizationRenamer";
import {Authorizations} from "../../../../../authorization/Authorizations";
import {userIsAdmin} from "../../../../../authorization/AuthorizationCheckers";
import {AuthContext} from "../../../../../contexts/AuthContext";
import UserValidator, {TFormError} from "../../logic/userValidator";
import UserFormDropdown from "./UserFormDropdown";
import UserFormInput from "./UserFormInput";

export type TUserRecord = {
    id?: string;
    firstName: string;
    lastName: string;
    email?: string;
    generatePassword?: boolean;
    password?: string;
    role: UserRole;
    isReadOnly: boolean;
    authorizations: string[];
    properties: { id: string, name: string, clientId: string }[];
    groups: {id: string; name: string;}[];
}

export interface IUserForm {
    isCreate?: boolean,
    forceGeneratePassword?: boolean,
    user: TUserRecord,
    loading: boolean,
    onClose: () => void,
    onChange: (updates: Partial<TUserRecord>) => void,
    onSubmit: () => void,
}

export default function UserForm(props: IUserForm): ReactElement {
    const {
        isCreate = false,
        user,
        loading,
        onClose,
        onChange,
        onSubmit,
    } = props;
    const [properties, setProperties] = useState<MinimalProperty[]>();
    const roles = Object.values(UserRole);
    const userValidator = new UserValidator();
    const [formErrors, setFormErrors] = useState<TFormError[]>([]);
    const [adminSelected, setAdminSelected] = useState<boolean>(user?.role == UserRole.Admin);
    const managesUsers = user.authorizations.includes(Authorizations.ManageUsers);
    const {user: currentUser} = useContext(AuthContext);
    const canEditRole = userIsAdmin(currentUser);

    function checkValidOnBlur(field: string): void {
        if (formErrors.find(x => x.field == field)) {
            setFormErrors(userValidator.validate(user, isCreate));
        }
    }

    function submit(): void {
        const errors = userValidator.validate(user, isCreate);

        if (errors.length == 0) {
            onSubmit();
        } else {
            setFormErrors(errors);
        }
    }

    const [loadAuthorizations, { data: authorizationsData, loading: authorizationsLoading }] = useListAuthorizationsLazyQuery({
        fetchPolicy: "no-cache"
    });
    const {loading: groupsLoading, data: groups} = useListGroupsQuery({
        fetchPolicy: "no-cache"
    });
    const {loading: userClientsLoading, data: userClients} = useListUserClientsQuery({
        fetchPolicy: "no-cache"
    });
    const {loading: propertiesLoading, data: propertiesData} = useListUserClientsPropertiesQuery({
        fetchPolicy: "no-cache"
    });

    useEffect(() => {
        loadAuthorizations();
    }, []);

    useEffect(() => {
        if(!propertiesData || propertiesLoading) {
            return;
        }
        let properties = propertiesData.listUserClientsProperties.map(p => asMinimalProperty(p));
        properties = properties.sort((a, b) => a.name.localeCompare(b.name));
        setProperties(properties);
    }, [propertiesData, propertiesLoading]);

    if (!authorizationsData || authorizationsLoading || !groups || groupsLoading
        || !userClients || userClientsLoading || !properties
    ) {
        return <></>;
    }

    const authorizationItems: {id: string, name: string, disabled: boolean}[] = [];
    for(const authorization of authorizationsData.listAuthorizations) {
        authorizationItems.push({
            id: authorization,
            name: formatterEnumToTitle(authorizationTitleRemapping(authorization)),
            disabled: managesUsers && authorization !== Authorizations.ManageUsers
        });
    }

    const userAuthorizations: {id: string, name:string}[] = [];
    for(const authorization of user.authorizations) {
        if(authorizationsData.listAuthorizations.includes(authorization)) {
            userAuthorizations.push({
                id: authorization,
                name: formatterEnumToTitle(authorizationTitleRemapping(authorization))
            });
        }
    }

    const userGroupsWithAuthorizations: Partial<UserGroupModel>[] = [];
    for(const group of user.groups) {
        const dbGroup = groups.listUserGroups.find(g => g.id === group.id);
        if (dbGroup) {
            userGroupsWithAuthorizations.push(dbGroup);
        }
    }

    function renderGroupRow(group: Partial<UserGroupModel>): ReactElement {
        const groupAuthorizations = group?.authorizations ?? [];
        return (
            <div key={group.id} className={css.list}>
                <h4 className={css.listHeader}>Group: {group.name}</h4>
                <UnorderedList>
                    {groupAuthorizations.map(a =>
                        <UnorderedList.Item key={a} className={css.listItem}>
                            {formatterEnumToTitle(authorizationTitleRemapping(a))}
                        </UnorderedList.Item>
                    )}
                </UnorderedList>
            </div>
        );
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Header>{isCreate ? "Add" : "Edit"} User</Header>
            <Body>
                <form className={css.userForm}>
                    <UserFormInput
                        label={"First Name"}
                        placeholder={"First Name"}
                        required
                        value={user.firstName}
                        error={formErrors.find(x => x.field == "firstName")?.message}
                        onChange={evt => onChange({ firstName: evt.target.value })}
                        onBlur={() => checkValidOnBlur('firstName')}
                    />

                    <UserFormInput
                        label={"Last Name"}
                        placeholder={"Last Name"}
                        required
                        value={user.lastName}
                        error={formErrors.find(x => x.field == "lastName")?.message}
                        onChange={evt => onChange({ lastName: evt.target.value })}
                        onBlur={() => checkValidOnBlur('lastName')}
                    />

                    {
                        isCreate &&
                        <>
                            <UserFormInput
                                label={"Email"}
                                placeholder={"Password"}
                                required
                                value={user.email}
                                error={formErrors.find(x => x.field == "email")?.message}
                                onChange={evt => onChange({ email: evt.target.value })}
                                onBlur={() => checkValidOnBlur('email')}
                            />

                            <Field>
                                <Toggle checked={props.forceGeneratePassword || (user.generatePassword ?? false)}
                                        disabled={props.forceGeneratePassword}
                                        onChange={evt => onChange({ generatePassword: evt.target.checked })}>
                                    <Label>Generate Password?</Label>
                                </Toggle>
                            </Field>
                            {!user.generatePassword && !props.forceGeneratePassword &&
                                <UserFormInput
                                    label={"Password"}
                                    placeholder={"Password"}
                                    required
                                    value={user.password}
                                    error={formErrors.find(x => x.field == "password")?.message}
                                    onChange={evt => onChange({ password: evt.target.value })}
                                    onBlur={() => checkValidOnBlur('password')}
                                />
                            }
                        </>
                    }

                    {canEditRole &&
                        <UserFormDropdown
                            label={"Role"}
                            required
                            selection={user.role}
                            options={roles}
                            error={formErrors.find(x => x.field == "role")?.message}
                            onSelect={item => {
                                setAdminSelected(item == UserRole.Admin);
                                onChange({ role: item });
                            }}
                            onBlur={() => checkValidOnBlur('role')}
                        />
                    }

                    {!adminSelected &&
                        <>
                            <Field>
                                <Toggle checked={user.isReadOnly}
                                    onChange={evt => onChange({ isReadOnly: evt.target.checked })}>
                                    <Label>Is Read Only?</Label>
                                </Toggle>
                            </Field>

                            <UserFormDropdown
                                    label={"Groups"}
                                    required={false}
                                    disabled={managesUsers}
                                    selection={user.groups}
                                    isMultiselect
                                    options={groups.listUserGroups.map(g => ({
                                        id: g.id,
                                        name: g.name
                                    }))}
                                    error={formErrors.find(x => x.field == "groups")?.message}
                                    onSelect={items => onChange({groups: items})}
                                    downshiftProps={{itemToString: (item: any) => item && item.id}}
                                    onBlur={() => checkValidOnBlur('groups')}
                                    popperModifiers={{
                                        flip: {
                                            boundariesElement: "window",
                                            behavior: ["top"],
                                        },
                                    }}
                            />

                            <UserFormDropdown
                                label={"Authorizations"}
                                required={false}
                                hint={managesUsers ? "Manage Users can't be combined with other authorizations, groups or properties." : undefined}
                                selection={userAuthorizations}
                                isMultiselect
                                options={authorizationItems}
                                error={formErrors.find(x => x.field == "authorizations")?.message}
                                onSelect={items => {
                                    const authorizations: string[] = items.map((i: any) => i.id);
                                    if(authorizations.includes(Authorizations.ManageUsers)) {
                                        onChange({ authorizations: [Authorizations.ManageUsers], properties: [], groups: [] });
                                    } else {
                                        onChange({ authorizations });
                                    }
                                }}
                                downshiftProps={{itemToString: (item: any) => item && item.id}}
                                onBlur={() => checkValidOnBlur('authorizations')}
                            />

                            <Accordion
                                isCompact
                                level={4}
                            >
                                <Accordion.Section>
                                    <Accordion.Header>
                                        <Accordion.Label className={css.listSectionHeader}>View Full User Permissions (Groups + Authorizations)</Accordion.Label>
                                    </Accordion.Header>
                                    <Accordion.Panel className={css.listBody}>
                                        {[...userGroupsWithAuthorizations, ...userAuthorizations].length === 0 ?
                                            <h4 className={css.listHeader}>No Permissions</h4>
                                            :
                                            <>
                                                {userGroupsWithAuthorizations.map(g => renderGroupRow(g))}
                                                {userAuthorizations.map(a => <h4 key={a.id} className={css.listHeader}>{a.name}</h4>)}
                                            </>
                                        }
                                    </Accordion.Panel>
                                </Accordion.Section>
                            </Accordion>

                            {
                                properties &&
                                    <>
                                        <Label className={css.userFormLabel} style={{marginBottom: ".5rem"}}>
                                            <span>Properties</span>
                                            {!managesUsers && <span className={css.required}>REQUIRED</span>}
                                        </Label>
                                        <CustomZDDropdown
                                            initialSelectedItems={user.properties.map(x => x.id)}
                                            applySelectedItems={function (items: string[]): void {
                                                const activeProperties = properties.filter(x => items.includes(x.id));
                                                onChange({ properties: activeProperties });
                                            }}
                                            openDropdownPlaceholder={"Properties"}
                                            closedDropdownPlaceholder={"Properties"}
                                            options={properties.map(p => ({value: p.id, label: p.name}))}
                                            isError={!!formErrors.find(x => x.field == "properties")}
                                            disabled={managesUsers}
                                            isMulti
                                            allOption
                                            errorMessage="At least one property is required."
                                            renderOption={(option) => {
                                                const clientId = properties.find(p => p.id == option.value)?.clientId;
                                                const client = clientId ? userClients.listUserClients.find(w => w.clientId == clientId) : undefined;
                                                return (
                                                    <>
                                                        <span>
                                                            {option.label}
                                                        </span>
                                                        {client &&
                                                        <>
                                                            <br/>
                                                            <span style={{color: "var(--primary-400)"}}>
                                                                {client.clientName}
                                                            </span>
                                                        </>
                                                        }
                                                    </>
                                                );
                                            }}
                                        />
                                    </>
                            }

                            <Accordion
                                isCompact
                                level={4}
                            >
                                <Accordion.Section>
                                    <Accordion.Header>
                                        <Accordion.Label className={css.listSectionHeader}>View All Assigned Properties</Accordion.Label>
                                    </Accordion.Header>
                                    <Accordion.Panel className={css.listBody}>
                                        {user.properties.length === 0 ?
                                            <h4 className={css.listHeader}>No Properties Assigned</h4>
                                            :
                                            user.properties.sortBy("name").map(p => <h4 key={p.id} className={css.listHeader}>{p.name}</h4>)
                                        }
                                    </Accordion.Panel>
                                </Accordion.Section>
                            </Accordion>
                        </>
                    }
                </form>
            </Body>
            <Separator />
            <Footer>
                <FooterItem>
                    <Button onClick={onClose} isBasic disabled={loading}>
                        Cancel
                    </Button>
                </FooterItem>
                <FooterItem>
                    <Button isPrimary onClick={submit} disabled={loading}>
                        {
                            loading
                                ? <Inline size={24} color={COLORS.POSITIVE_COLOR} aria-label="loading"/>
                                : <span>Save</span>
                        }
                    </Button>
                </FooterItem>
            </Footer>
        </ThemeProvider>
    );

}